import React, { useEffect, useState, useRef, useCallback } from 'react'
import { supabase, AUTO_REFRESH_ENABLED } from '../supabaseClient'
import Calendar from './Calendar'
import ManageUsers, { TIMEZONES } from './ManageUsers'
import Directory from './Directory'
import PaymentsAdmin from './PaymentsAdmin'
import ManageCourses from './ManageCourses'
import Resources from './Resources'
import RevenueChart from './RevenueChart'
import EnrolStudent from './EnrolStudent'
import ScheduleClasses from './ScheduleClasses'
import ActivityLog from './ActivityLog'
import BatchResetTool from './BatchResetTool'
import BulkCancel from './BulkCancel'
import BulkReschedule from './BulkReschedule'
import ClockChange from './ClockChange'
import ClassReimbursements from './ClassReimbursements'
import EnrollExisting from './EnrollExisting'
import MarketingHub from './MarketingHub'
import ZoomRecordings from './ZoomRecordings'
import Candidates from './Candidates'
import ReminderLog from './ReminderLog'
import SystemHealth from './SystemHealth'
import Layout, { PageHeader, Grid4, MetricCard, Panel, TwoCol, Row, Pill, Empty, Lbl, Inp, Btn, Err, Ok, useTabs, Keep } from './Layout'
import { sendEmail } from '../emailService'
import RescheduleManager from './RescheduleManager'
import ScheduleDemo, { ScheduleDemoModal } from './ScheduleDemo'
import LeadsDashboard from './LeadsDashboard'
import ClassScheduleView from './ClassScheduleView'
import TeacherAvailability from './TeacherAvailability'
import AdminSupportInbox from './AdminSupportInbox'

const STATUSES = [
  'New', 'Contacted', 'Demo Scheduled', 'Enrolled',
  'Follow Up', 'Not Interested', 'Dead / Unresponsive', 'Lost',
]

const STATUS_CONFIG = {
  'New':                 { bg:'rgba(30,144,255,0.15)',   c:'#5aabff',  icon:'🆕' },
  'Contacted':           { bg:'rgba(232,124,30,0.15)',   c:'#f4a335',  icon:'📞' },
  'Demo Scheduled':      { bg:'rgba(139,92,246,0.15)',   c:'#a78bfa',  icon:'📅' },
  'Enrolled':            { bg:'rgba(16,185,129,0.15)',   c:'#10b981',  icon:'✅' },
  'Follow Up':           { bg:'rgba(96,165,250,0.15)',   c:'#60a5fa',  icon:'🔄' },
  'Not Interested':      { bg:'rgba(248,113,113,0.12)',  c:'#f87171',  icon:'👎' },
  'Dead / Unresponsive': { bg:'rgba(100,100,100,0.15)',  c:'#6b7280',  icon:'💀' },
  'Lost':                { bg:'rgba(239,68,68,0.12)',    c:'#ef4444',  icon:'❌' },
}

const SOURCE_COLOR = {
  'Google Ads':    { bg:'rgba(66,133,244,0.15)',  c:'#4285f4' },
  'Facebook Ads':  { bg:'rgba(24,119,242,0.15)',  c:'#1877f2' },
  'Instagram Ads': { bg:'rgba(193,53,132,0.15)',  c:'#c13584' },
  'Landing Page':  { bg:'rgba(16,185,129,0.15)',  c:'#10b981' },
  'Sales Team':    { bg:'rgba(232,124,30,0.15)',  c:'#f4a335' },
  'Bulk Upload':   { bg:'rgba(139,92,246,0.15)',  c:'#a78bfa' },
}

function SourceBadge({ source }) {
  const s = SOURCE_COLOR[source] || { bg:'rgba(100,100,100,0.15)', c:'#888' }
  return (
    <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'10px', background:s.bg, color:s.c, whiteSpace:'nowrap', flexShrink:0 }}>
      {source || 'Unknown'}
    </span>
  )
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { bg:'rgba(100,100,100,0.15)', c:'#888', icon:'•' }
  return (
    <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'10px', background:cfg.bg, color:cfg.c, whiteSpace:'nowrap', flexShrink:0, display:'inline-flex', alignItems:'center', gap:'4px' }}>
      {cfg.icon} {status}
    </span>
  )
}

function LeadsStatusBar({ leads }) {
  if (!leads.length) return null
  const total = leads.length
  const counts = {}
  STATUSES.forEach(s => { counts[s] = leads.filter(l => l.status === s).length })
  return (
    <div style={{ marginBottom:'16px' }}>
      <div style={{ display:'flex', height:'8px', borderRadius:'8px', overflow:'hidden', marginBottom:'10px', background:'rgba(var(--fg-rgb),0.05)' }}>
        {STATUSES.map(s => {
          const pct = (counts[s] / total) * 100
          if (!pct) return null
          const cfg = STATUS_CONFIG[s]
          return <div key={s} style={{ width:`${pct}%`, background:cfg.c, transition:'width 0.5s ease' }} title={`${s}: ${counts[s]}`}/>
        })}
      </div>
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
        {STATUSES.map(s => {
          if (!counts[s]) return null
          const cfg = STATUS_CONFIG[s]
          return (
            <div key={s} style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a60))', padding:'3px 10px', borderRadius:'20px', background:cfg.bg }}>
              <span style={{ color:cfg.c }}>{cfg.icon}</span>
              <span style={{ color:cfg.c }}>{s}</span>
              <span style={{ background:'rgba(var(--fg-rgb),0.15)', color:'white', borderRadius:'10px', padding:'0 5px', fontSize:'10px', fontWeight:800 }}>{counts[s]}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return mobile
}

export default function AdminDash({ profile }) {
  const [stats,      setStats]      = useState({ users:0, students:0, leads:0, newLeads:0, revenue:0 })
  const [leads,      setLeads]      = useState([])
  // FIX #5: users list now paginates — no hard limit of 20
  const [users,      setUsers]      = useState([])
  const [classes,    setClasses]    = useState([])
  const [allClasses, setAllClasses] = useState([])
  const [loading,    setLoading]    = useState(true)
  const { tab: activeSection, go: setActiveSection, has: visited } = useTabs('overview')
  const [demoModal,  setDemoModal]  = useState(null)
  const isMobile = useIsMobile()

  const load = useCallback(async () => {
    setLoading(true)
    const [
      { count: usersCount },
      { count: studentsCount },
      { count: leadsCount },
      { count: newLeadsCount },
      { data: allLeads },
      { data: recentUsers },
      { data: recentClasses },
      { data: payments },
    ] = await Promise.all([
      supabase.from('profiles').select('*', { count:'exact', head:true }),
      supabase.from('profiles').select('*', { count:'exact', head:true }).eq('role','student'),
      supabase.from('leads').select('*', { count:'exact', head:true }),
      supabase.from('leads').select('*', { count:'exact', head:true }).eq('status','New'),
      supabase.from('leads').select('*').order('created_at', { ascending:false }).limit(100),
      // FIX #5: fetch all users, not just 20
      supabase.from('profiles').select('*').order('created_at', { ascending:false }),
      supabase.from('classes').select('*').order('created_at', { ascending:false }).limit(10),
      supabase.from('payments').select('amount,status').eq('status','paid'),
    ])
    const revenue = (payments||[]).reduce((a,p) => a+(p.amount||0), 0)
    setStats({ users:usersCount, students:studentsCount, leads:leadsCount, newLeads:newLeadsCount, revenue })
    setLeads(allLeads || [])
    setUsers(recentUsers || [])
    setClasses(recentClasses || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (activeSection === 'classes') loadAllClassesWithStudents()
  }, [activeSection])

  async function loadAllClassesWithStudents() {
    const { data: classesData } = await supabase.from('classes').select('*')
      .neq('is_cancelled', true)
      .order('class_date', { ascending: true })
      .order('start_time', { ascending: true })

    const classes = classesData || []
    const classIds = classes.map(c => c.id)
    if (!classIds.length) { setAllClasses([]); return }

    const { data: enr } = await supabase.from('enrollments').select('class_id, student_id').in('class_id', classIds)
    const studentIds = [...new Set((enr || []).map(e => e.student_id).filter(Boolean))]
    const { data: studentsData } = studentIds.length
      ? await supabase.from('profiles').select('id, full_name').in('id', studentIds)
      : { data: [] }
    const studentById = Object.fromEntries((studentsData || []).map(s => [s.id, s.full_name]))

    const namesByClass = {}
    ;(enr || []).forEach(e => {
      const name = studentById[e.student_id]
      if (!name) return
      if (!namesByClass[e.class_id]) namesByClass[e.class_id] = []
      namesByClass[e.class_id].push(name)
    })

    setAllClasses(classes.map(c => ({ ...c, student_names: (namesByClass[c.id] || []).join(', ') })))
  }

  useEffect(() => {
    if (!AUTO_REFRESH_ENABLED) return
    let debounceTimer = null
    const debouncedLoad = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => load(), 1500)
    }
    const ch = supabase.channel('admin-all-rt')
      .on('postgres_changes', { event:'*', schema:'public', table:'leads'    }, debouncedLoad)
      .on('postgres_changes', { event:'*', schema:'public', table:'profiles' }, debouncedLoad)
      .on('postgres_changes', { event:'*', schema:'public', table:'classes'  }, debouncedLoad)
      .on('postgres_changes', { event:'*', schema:'public', table:'payments' }, debouncedLoad)
      .subscribe()
    return () => {
      clearTimeout(debounceTimer)
      supabase.removeChannel(ch)
    }
  }, [load])

  async function deleteUser(id, name) {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return
    await supabase.from('profiles').delete().eq('id', id)
    try { await supabase.functions.invoke('delete-user', { body: { userId: id } }) } catch(e) {}
    load()
  }

  // FIX #8: deleteClass now deletes from events by class ID (not title string)
  async function deleteClass(id, title) {
    if (!window.confirm(`Delete class "${title}"?`)) return
    await supabase.from('classes').delete().eq('id', id)
    // Delete matching event by class_id if the events table has that column,
    // otherwise fall back to title+event_type to avoid wiping unrelated events
    const { error } = await supabase.from('events').delete().eq('class_id', id)
    if (error) {
      // Fallback: match on title AND event_type to minimise blast radius
      await supabase.from('events').delete()
        .eq('title', title)
        .eq('event_type', 'class')
    }
    load()
    loadAllClassesWithStudents()
  }

  async function sendFeeReminder(studentId, studentName, studentEmail, courseName, amount, currency) {
    if (!studentEmail) return alert('No email for this student.')
    await sendEmail('fee_reminder', studentEmail, {
      name: studentName, courseName: courseName || 'Your course',
      amount: amount || 0, currency: currency || 'INR',
      paymentLink: null, invoiceNo: 'REMINDER-' + Date.now().toString().slice(-6), dueDate: null,
    })
    alert(`✓ Fee reminder sent to ${studentName}`)
  }

  async function changeRole(id, role) {
    await supabase.from('profiles').update({ role }).eq('id', id)
    load()
  }

  async function updateLeadStatus(id, status) {
    await supabase.from('leads').update({ status }).eq('id', id)
    setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l))
  }

  const sel = {
    background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.12)',
    color:'rgba(var(--fg-rgb),var(--a70))', borderRadius:'6px',
    padding:'4px 6px', fontSize:'11px', cursor:'pointer', outline:'none',
    maxWidth: isMobile ? '100px' : '140px',
  }

  return (
    <Layout profile={profile} pageTitle="Dashboard" activeTab={activeSection} onTabChange={setActiveSection}>
      <PageHeader title="Platform Overview" subtitle="Live metrics — full admin access." />

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? '10px' : '12px', marginBottom:'20px' }}>
        <MetricCard icon="👥" label="Total Users"   value={stats.users}
          onClick={() => setActiveSection('users')} />
        <MetricCard icon="🎓" label="Students"      value={stats.students}
          onClick={() => setActiveSection('directory')} />
        <MetricCard icon="🆕" label="New Leads"     value={stats.newLeads}
          accentColor={stats.newLeads > 0 ? '#f4a335' : 'var(--fg-solid)'}
          onClick={() => setActiveSection('leads')} />
      <MetricCard icon="💰" label="Revenue"       value={`$${(stats.revenue||0).toLocaleString('en-US')}`} accentColor="#10b981"
          onClick={() => setActiveSection('payments')} />
      </div>

      {visited('overview') && (
        <Keep show={activeSection === 'overview'}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'14px', marginBottom:'14px' }}>
            <Panel title="Recent Leads">
              {loading ? <Empty msg="Loading..." /> : leads.slice(0,6).length === 0 ? <Empty msg="No leads yet." /> :
                leads.slice(0,6).map(l => (
                  <div key={l.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'9px 0', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.05)', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <div style={{ width:'28px', height:'28px', borderRadius:'50%', flexShrink:0, background:'rgba(139,92,246,0.2)', color:'#a78bfa', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:800 }}>
                      {(l.full_name||'?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.full_name}</div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{l.phone||l.email||'—'}</div>
                    </div>
                    <StatusBadge status={l.status} />
                  </div>
                ))}
              <button onClick={() => setActiveSection('leads')} style={{ marginTop:'10px', fontSize:'12px', color:'#5aabff', background:'none', border:'none', cursor:'pointer', padding:0 }}>
                View all {stats.leads} leads →
              </button>
            </Panel>

            <Panel title="Users — Set Role">
              {loading ? <Empty msg="Loading..." /> : users.length === 0 ? <Empty msg="No users yet." /> :
                // Show first 20 in overview panel — full list under Users tab
                users.slice(0,20).map(u => (
                  <div key={u.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 0', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.05)' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.full_name || u.email}</div>
                      {!isMobile && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{u.email}</div>}
                    </div>
                    <select value={u.role} onChange={e => changeRole(u.id, e.target.value)} style={sel}>
                      <option value="student">Student</option>
                      <option value="teacher">Teacher</option>
                      <option value="sales">Sales</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                ))}
              {users.length > 20 && (
                <button onClick={() => setActiveSection('users')} style={{ marginTop:'10px', fontSize:'12px', color:'#5aabff', background:'none', border:'none', cursor:'pointer', padding:0 }}>
                  View all {users.length} users →
                </button>
              )}
            </Panel>
          </div>

          <Panel title="Upcoming Classes" style={{ marginBottom:'14px' }}>
            {loading ? <Empty msg="Loading..." /> : classes.filter(c=>c.class_date>=new Date().toISOString().slice(0,10)).length === 0 ? <Empty msg="No upcoming classes." /> :
              <div style={{ display:'grid', gap:'6px' }}>
                {classes
                  .filter(c=>c.class_date>=new Date().toISOString().slice(0,10))
                  .sort((a,b)=>a.class_date.localeCompare(b.class_date)||(a.start_time||'').localeCompare(b.start_time||''))
                  .slice(0,6)
                  .map(c => {
                    const today = new Date().toISOString().slice(0,10)
                    const isToday = c.class_date === today
                    return (
                      <div key={c.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', background: isToday?'rgba(239,68,68,0.08)':'rgba(var(--fg-rgb),0.03)', border:`0.5px solid ${isToday?'rgba(239,68,68,0.2)':'rgba(var(--fg-rgb),0.06)'}`, borderRadius:'10px' }}>
                        <div style={{ fontSize:'11px', fontWeight:700, color: isToday?'#f87171':'#5aabff', width:'50px', flexShrink:0 }}>
                          {c.start_time ? new Date('2000-01-01T'+c.start_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—'}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.title}</div>
                          <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'1px' }}>{c.class_date}{c.teacher_name&&` · 👨‍🏫 ${c.teacher_name}`}</div>
                        </div>
                        {isToday && <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background:'rgba(239,68,68,0.15)', color:'#f87171' }}>TODAY</span>}
                        {c.meet_link && <a href={c.meet_link} target="_blank" rel="noreferrer" style={{ fontSize:'10px', fontWeight:700, padding:'3px 9px', borderRadius:'7px', background:'rgba(30,144,255,0.12)', color:'#5aabff', textDecoration:'none' }}>🔗</a>}
                      </div>
                    )
                  })}
              </div>}
            <button onClick={()=>setActiveSection('classes')} style={{ marginTop:'10px', fontSize:'12px', color:'#5aabff', background:'none', border:'none', cursor:'pointer', padding:0 }}>
              View all classes →
            </button>
          </Panel>

          <RevenueChart />
          <BulkUploadAdmin profile={profile} isMobile={isMobile} />
        </Keep>
      )}

      {visited('leads') && (
        <Keep show={activeSection === 'leads'}>
          <PageHeader title="All Leads" subtitle="Manage and track every lead." />
          {!loading && <LeadsStatusBar leads={leads} />}
          <LeadsDashboard profile={profile} />
        </Keep>
      )}

      {visited('adleads') && (
        <Keep show={activeSection === 'adleads'}>
          <PageHeader title="Leads from Ads" subtitle="Leads submitted via your /book form." />
          {!loading && <LeadsStatusBar leads={leads.filter(l => ['Google Ads','Facebook Ads','Instagram Ads','Landing Page'].includes(l.source))} />}
          <Panel>
            {leads.filter(l => ['Google Ads','Facebook Ads','Instagram Ads','Landing Page'].includes(l.source)).map(l => (
              <div key={l.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 0', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.05)', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <div style={{ width:'30px', height:'30px', borderRadius:'50%', flexShrink:0, background:'rgba(30,144,255,0.2)', color:'#5aabff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:800 }}>
                  {(l.full_name||'?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.full_name}</div>
                  <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'1px' }}>{l.phone} {!isMobile && l.email && `· ${l.email}`}</div>
                </div>
                {!isMobile && <SourceBadge source={l.source} />}
                <select value={l.status||'New'} onChange={e => updateLeadStatus(l.id, e.target.value)} style={sel}>
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s]?.icon} {s}</option>)}
                </select>
                <button onClick={() => setDemoModal(l)} style={{ fontSize:'11px', fontWeight:700, padding:'5px 10px', borderRadius:'8px', border:'none', cursor:'pointer', background:'rgba(139,92,246,0.15)', color:'#a78bfa', fontFamily:'inherit', flexShrink:0, whiteSpace:'nowrap' }}>
                  🎯 Demo
                </button>
              </div>
            ))}
          </Panel>
        </Keep>
      )}

      {visited('users') && (
        <Keep show={activeSection === 'users'}>
          <UsersSection
            profile={profile}
            users={users}
            loading={loading}
            isMobile={isMobile}
            onRoleChange={changeRole}
            onReload={load}
          />
        </Keep>
      )}

      {visited('classes') && (
        <Keep show={activeSection === 'classes'}>
          <PageHeader title="All Classes" subtitle="Sorted by date & time."/>
          {loading ? <Empty msg="Loading..."/> : (
            <ClassScheduleView classes={allClasses} showTeacher role="admin" onDelete={deleteClass}/>
          )}
        </Keep>
      )}

      {visited('reminders') && (
        <Keep show={activeSection === 'reminders'}>
          <PageHeader title="WhatsApp Reminders" subtitle="Automated 2-hour-before-class reminders sent to students." />
          <ReminderLog />
        </Keep>
      )}

      {visited('directory') && (
        <Keep show={activeSection === 'directory'}>
          <PageHeader title="Directory" subtitle="Look up class, student & teacher details — including emails." />
          <Directory />
        </Keep>
      )}

      {visited('candidates') && (
        <Keep show={activeSection === 'candidates'}>
          <PageHeader title="Candidates" subtitle="Upload CVs, run interviews, and manage the hiring pipeline." />
          <Candidates profile={profile} />
        </Keep>
      )}

      {visited('support-inbox') && (
        <Keep show={activeSection === 'support-inbox'}>
          <PageHeader title="Support Inbox" subtitle="Messages from students, teachers and sales. Replies appear in their support chat." />
          <AdminSupportInbox profile={profile} />
        </Keep>
      )}

      {visited('sales-perms') && (
        <Keep show={activeSection === 'sales-perms'}>
          <PageHeader title="Sales Permissions" subtitle="Choose what each sales team member can see and do." />
          <SalesPermissions />
        </Keep>
      )}

      {visited('feereminder')   && <Keep show={activeSection === 'feereminder'}><PageHeader title="Fee Reminders"/><FeeReminderSection sendFeeReminder={sendFeeReminder} isMobile={isMobile}/></Keep>}
      {visited('activitylog')   && <Keep show={activeSection === 'activitylog'}><PageHeader title="Activity Log"/><ActivityLog/></Keep>}
      {visited('recordings')    && <Keep show={activeSection === 'recordings'}><PageHeader title="Class Recordings"/><ZoomRecordings profile={profile}/></Keep>}
      {visited('schedule-demo') && <Keep show={activeSection === 'schedule-demo'}><PageHeader title="Schedule Demo"/><ScheduleDemo profile={profile}/></Keep>}
      {visited('reschedule')    && <Keep show={activeSection === 'reschedule'}><PageHeader title="Reschedule"/><RescheduleManager profile={profile}/></Keep>}
      {visited('bulkcancel')    && <Keep show={activeSection === 'bulkcancel'}><PageHeader title="Bulk Cancel"/><BulkCancel/></Keep>}
      {visited('bulkreschedule')&& <Keep show={activeSection === 'bulkreschedule'}><PageHeader title="Bulk Reschedule"/><BulkReschedule/></Keep>}
      {visited('clockchange')   && <Keep show={activeSection === 'clockchange'}><PageHeader title="Clock Change" subtitle="Keep UK/US students' local class time after the clocks change."/><ClockChange/></Keep>}
      {visited('reimbursements')&& <Keep show={activeSection === 'reimbursements'}><PageHeader title="Class Reimbursements" subtitle="Grant a makeup class when a scheduled class wasn't actually taken."/><ClassReimbursements profile={profile}/></Keep>}
      {visited('enrollexisting')&& <Keep show={activeSection === 'enrollexisting'}><PageHeader title="Enroll in Existing Classes"/><EnrollExisting/></Keep>}
      {visited('batchreset')    && <Keep show={activeSection === 'batchreset'}><PageHeader title="Batch Reset"/><BatchResetTool/></Keep>}
      {visited('marketing')     && <Keep show={activeSection === 'marketing'}><PageHeader title="Marketing Hub"/><MarketingHub profile={profile}/></Keep>}
      {visited('enrol')         && <Keep show={activeSection === 'enrol'}><PageHeader title="Enrol Student"/><EnrolStudent/></Keep>}
      {visited('payments')      && <Keep show={activeSection === 'payments'}><PageHeader title="Payments"/><PaymentsAdmin profile={profile}/></Keep>}
      {visited('schedule')      && <Keep show={activeSection === 'schedule'}><PageHeader title="Schedule Classes"/><ScheduleClasses profile={profile}/></Keep>}
      {visited('availability')  && <Keep show={activeSection === 'availability'}><PageHeader title="Teacher Availability"/><TeacherAvailability profile={profile}/></Keep>}
      {visited('courses')       && <Keep show={activeSection === 'courses'}><PageHeader title="Courses"/><ManageCourses/></Keep>}
      {visited('resources')     && <Keep show={activeSection === 'resources'}><PageHeader title="Resources"/><Resources profile={profile}/></Keep>}
      {visited('calendar')      && <Keep show={activeSection === 'calendar'}><PageHeader title="Calendar"/><Calendar profile={profile}/></Keep>}
      {visited('health')        && <Keep show={activeSection === 'health'}><PageHeader title="System Health" subtitle="Live status of everything the app depends on."/><SystemHealth/></Keep>}

      {demoModal && (
        <ScheduleDemoModal lead={demoModal} onClose={() => setDemoModal(null)} onSaved={() => { setDemoModal(null); load() }}/>
      )}
    </Layout>
  )
}

// ── SALES PERMISSIONS ─────────────────────────────────────────────────────────
// Per-salesperson switches for what their dashboard shows. Stored on the
// profile; only admins can change them (enforced by guard_profile_role).
const SALES_SWITCHES = [
  { key:'sales_can_view_leads',    label:'Leads',          hint:'See and update the lead list',        defaultOn:true  },
  { key:'sales_can_schedule_demo', label:'Schedule Demo',  hint:'Book demos; see students (info only)', defaultOn:true  },
  { key:'can_schedule_classes',    label:'Schedule Class', hint:'Create classes and enrol students',   defaultOn:false },
  { key:'sales_can_see_contact',   label:'Phone & email',  hint:'See leads\' phone numbers and emails', defaultOn:true  },
]

function SalesPermissions() {
  const [salesUsers, setSalesUsers] = useState([])
  const [loading,    setLoading]    = useState(true)
  const [saving,     setSaving]     = useState(null)
  const [ok,         setOk]         = useState('')
  const [err,        setErr]        = useState('')

  useEffect(() => { loadSalesUsers() }, [])

  async function loadSalesUsers() {
    setLoading(true)
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email, ' + SALES_SWITCHES.map(s => s.key).join(', '))
      .eq('role', 'sales')
      .order('full_name')
    if (error) setErr(error.message.includes('sales_can_')
      ? 'Run SUPABASE_SALES_ACCESS_AND_STUDENT_ID.sql in Supabase first, then reload.'
      : error.message)
    setSalesUsers(data || [])
    setLoading(false)
  }

  const isOn = (user, sw) => user[sw.key] ?? sw.defaultOn

  async function toggle(user, sw) {
    const next = !isOn(user, sw)
    setSaving(`${user.id}:${sw.key}`)
    setOk(''); setErr('')
    const { error } = await supabase.from('profiles').update({ [sw.key]: next }).eq('id', user.id)
    if (error) setErr(error.message)
    else {
      setSalesUsers(prev => prev.map(u => u.id === user.id ? { ...u, [sw.key]: next } : u))
      setOk(`✓ ${sw.label} ${next ? 'turned on' : 'turned off'} for ${user.full_name || user.email}. They see the change next time they open the dashboard.`)
      setTimeout(() => setOk(''), 4000)
    }
    setSaving(null)
  }

  if (loading) return <div style={{ color:'rgba(var(--fg-rgb),var(--a30))', padding:'20px' }}>Loading sales users…</div>

  return (
    <div style={{ maxWidth:'760px' }}>
      <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'16px', lineHeight:1.6 }}>
        Choose what each sales team member can see and do. Support is always available.
      </div>

      {ok && <div style={{ marginBottom:'14px', padding:'10px 16px', background:'rgba(16,185,129,0.12)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:'10px', fontSize:'13px', color:'#34d399' }}>{ok}</div>}
      {err && <div style={{ marginBottom:'14px', padding:'10px 16px', background:'rgba(239,68,68,0.1)', border:'0.5px solid rgba(239,68,68,0.3)', borderRadius:'10px', fontSize:'13px', color:'#f87171' }}>{err}</div>}

      {salesUsers.length === 0 ? (
        <div style={{ color:'rgba(var(--fg-rgb),var(--a30))', padding:'30px', textAlign:'center', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px' }}>
          No sales users found. Add users with the Sales role first.
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {salesUsers.map(user => (
            <div key={user.id} style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.08)', borderRadius:'14px', padding:'14px 18px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'15px', fontWeight:800, color:'#fff', flexShrink:0 }}>
                  {(user.full_name || '?')[0].toUpperCase()}
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:'14px', color:'var(--fg-solid)' }}>{user.full_name || '—'}</div>
                  <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>{user.email}</div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:'8px' }}>
                {SALES_SWITCHES.map(sw => {
                  const on = isOn(user, sw)
                  const busy = saving === `${user.id}:${sw.key}`
                  return (
                    <button key={sw.key} type="button" onClick={() => toggle(user, sw)} disabled={busy} title={sw.hint}
                      style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'10px', cursor: busy ? 'wait' : 'pointer', fontFamily:'inherit', textAlign:'left',
                        background: on ? 'rgba(16,185,129,0.1)' : 'rgba(var(--fg-rgb),0.04)',
                        border: `0.5px solid ${on ? 'rgba(16,185,129,0.3)' : 'rgba(var(--fg-rgb),0.1)'}`, opacity: busy ? 0.6 : 1 }}>
                      <span style={{ width:'30px', height:'17px', borderRadius:'9px', background: on ? '#10b981' : 'rgba(var(--fg-rgb),0.15)', position:'relative', flexShrink:0 }}>
                        <span style={{ position:'absolute', top:'2px', left: on ? '15px' : '2px', width:'13px', height:'13px', borderRadius:'50%', background:'#fff', transition:'left .15s' }}/>
                      </span>
                      <span style={{ fontSize:'12px', fontWeight:600, color: on ? '#34d399' : 'rgba(var(--fg-rgb),var(--a50))' }}>{sw.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── BULK UPLOAD ───────────────────────────────────────────────────────────────
function BulkUploadAdmin({ profile, isMobile }) {
  const [csv, setCsv] = useState('')
  const [preview, setPreview] = useState([])
  const [uploading, setUploading] = useState(false)
  const [ok, setOk] = useState('')
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(false)
  const fileRef = useRef()

  function parseCSV(text) {
    const cleaned = text.replace(/^\uFEFF/, '').trim()
    const lines = cleaned.split('\n').filter(l => l.trim())
    if (lines.length < 2) return []

    function splitCSVLine(line) {
      const result = []
      let cur = '', inQuote = false
      for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (ch === '"') { inQuote = !inQuote }
        else if (ch === ',' && !inQuote) { result.push(cur.trim()); cur = '' }
        else cur += ch
      }
      result.push(cur.trim())
      return result
    }

    const headers = splitCSVLine(lines[0]).map(h =>
      h.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim()
    )

    function normalisePhone(p) {
      if (!p) return ''
      let n = p.replace(/[^0-9]/g, '')
      if (n.startsWith('91') && n.length === 12) n = n.slice(2)
      return n
    }

    return lines.slice(1).map(line => {
      const vals = splitCSVLine(line)
      const obj = {}
      headers.forEach((h, i) => obj[h] = (vals[i] || '').trim().replace(/^"|"$/g,''))
      const name     = obj['name'] || obj['full name'] || obj['fullname'] || ''
      const email    = obj['email'] || obj['email address'] || ''
      const rawPhone = obj['phone'] || obj['mobile'] || obj['whatsapp number'] ||
                       obj['whatsapp'] || obj['phone number'] || obj['contact'] ||
                       obj['secondary phone number'] || ''
      const phone    = normalisePhone(rawPhone)
      const course   = obj['course'] || obj['course interest'] || obj['subject'] || ''
      const source   = obj['source'] || 'Bulk Upload'
      return { full_name: name, phone, email, course_interest: course, source }
    }).filter(r => r.full_name || r.email || r.phone)
  }

  async function uploadLeads() {
    if (!preview.length) return setErr('No valid leads to upload.')
    setUploading(true); setErr(''); setOk('')

    function norm(p) {
      if (!p) return ''
      let n = String(p).replace(/[^0-9]/g, '')
      if (n.startsWith('91') && n.length === 12) n = n.slice(2)
      return n
    }

    const { data: existingLeads } = await supabase.from('leads').select('phone,email')
    const existingPhones = new Set((existingLeads || []).map(r => norm(r.phone)).filter(Boolean))
    const existingEmails = new Set((existingLeads || []).map(r => (r.email||'').toLowerCase()).filter(Boolean))

    const newLeads = preview.filter(r => {
      const phoneExists = r.phone && existingPhones.has(norm(r.phone))
      const emailExists = r.email && existingEmails.has(r.email.toLowerCase())
      return !phoneExists && !emailExists
    })

    const skipped = preview.length - newLeads.length
    if (!newLeads.length) {
      setOk(`⚠ All ${preview.length} leads already exist — nothing new to upload.`)
      setUploading(false)
      return
    }

    const { error } = await supabase.from('leads').insert(
      newLeads.map(r => ({ ...r, assigned_to: profile.id, status: 'New' }))
    )
    if (error) setErr(error.message)
    else {
      setOk(`✓ ${newLeads.length} new leads uploaded!${skipped > 0 ? ` (${skipped} duplicates skipped)` : ''}`)
      setPreview([]); setCsv('')
      if (fileRef.current) fileRef.current.value = ''
    }
    setUploading(false)
  }

  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'1rem 1.1rem' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:open?'14px':0 }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a70))' }}>📤 Bulk Lead Upload</div>
          <button onClick={() => setOpen(o=>!o)} style={{ fontSize:'12px', fontWeight:600, padding:'5px 14px', borderRadius:'8px', background:open?'rgba(var(--fg-rgb),0.07)':'rgba(30,144,255,0.15)', color:open?'rgba(var(--fg-rgb),var(--a40))':'#5aabff', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
            {open ? 'Close ✕' : '+ Upload CSV'}
          </button>
        </div>
        {open && (
          <>
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div>
                <Lbl>Upload CSV File</Lbl>
                <input ref={fileRef} type="file" accept=".csv,.txt" onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>{setCsv(ev.target.result);setPreview(parseCSV(ev.target.result))};r.readAsText(f)}} style={{ width:'100%', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'10px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))', outline:'none', boxSizing:'border-box' }}/>
              </div>
              <div>
                <Lbl>Or Paste CSV</Lbl>
                <textarea value={csv} onChange={e=>{setCsv(e.target.value);setPreview(parseCSV(e.target.value))}} rows={4} placeholder="name,phone,email,course" style={{ width:'100%', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'10px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))', outline:'none', resize:'vertical', fontFamily:'monospace', boxSizing:'border-box' }}/>
              </div>
            </div>
            {preview.length > 0 && <div style={{ fontSize:'12px', color:'#5aabff', marginBottom:'8px' }}>{preview.length} leads ready</div>}
            <Err msg={err}/><Ok msg={ok}/>
            <Btn busy={uploading} onClick={uploadLeads}>Upload {preview.length > 0 ? `${preview.length} Leads` : 'Leads'}</Btn>
          </>
        )}
      </div>
    </div>
  )
}

// ── FEE REMINDER SECTION ──────────────────────────────────────────────────────
function FeeReminderSection({ sendFeeReminder, isMobile }) {
  const [payments, setPayments] = React.useState([])
  const [loading,  setLoading]  = React.useState(true)
  const [sent,     setSent]     = React.useState({})

  React.useEffect(() => {
    supabase.from('payments').select('*').in('status',['pending','overdue']).order('created_at',{ascending:false})
      .then(({data}) => { setPayments(data||[]); setLoading(false) })
  }, [])

  async function remind(p) {
    setSent(s=>({...s,[p.id]:'sending'}))
    await sendFeeReminder(p.student_id,p.student_name,p.student_email,p.course_name,p.amount,p.currency)
    setSent(s=>({...s,[p.id]:'sent'}))
    setTimeout(()=>setSent(s=>({...s,[p.id]:null})),4000)
  }

  const sym = c => (c==='INR'||!c)?'₹':'$'
  const sc = { pending:'#f4a335', overdue:'#f87171' }

  if (loading) return <Empty msg="Loading..."/>
  if (!payments.length) return <div style={{textAlign:'center',padding:'3rem',color:'rgba(var(--fg-rgb),var(--a30))'}}>🎉 No pending payments!</div>

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px', flexWrap:'wrap', gap:'10px' }}>
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
          <div style={{ padding:'6px 12px', borderRadius:'12px', background:'rgba(244,163,53,0.1)', fontSize:'12px', fontWeight:700, color:'#f4a335' }}>⏳ {payments.filter(p=>p.status==='pending').length} Pending</div>
          <div style={{ padding:'6px 12px', borderRadius:'12px', background:'rgba(248,113,113,0.1)', fontSize:'12px', fontWeight:700, color:'#f87171' }}>🚨 {payments.filter(p=>p.status==='overdue').length} Overdue</div>
        </div>
        <button onClick={async()=>{const e=payments.filter(p=>p.student_email);if(!e.length)return alert('No emails found');if(!window.confirm(`Send to ${e.length} students?`))return;for(const p of e)await remind(p)}} style={{ padding:'8px 16px', background:'linear-gradient(135deg,#f4a335,#ef4444)', color:'#fff', fontSize:'12px', fontWeight:700, border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
          🔔 Send All ({payments.filter(p=>p.student_email).length})
        </button>
      </div>
      <div style={{ display:'grid', gap:'8px' }}>
        {payments.map(p => (
          <div key={p.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <div style={{ width:'36px', height:'36px', borderRadius:'50%', flexShrink:0, background:'rgba(244,163,53,0.15)', color:'#f4a335', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:800 }}>
              {(p.student_name||'?').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{p.student_name||'—'}</div>
              <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>{p.student_email||'⚠ No email'} · {p.course_name||'Course'}</div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:'15px', fontWeight:800, color:sc[p.status]||'var(--fg-solid)' }}>{sym(p.currency)}{(p.amount||0).toLocaleString('en-IN')}</div>
              <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 8px', borderRadius:'10px', background:`${sc[p.status]||'#888'}18`, color:sc[p.status]||'#aaa', textTransform:'uppercase' }}>{p.status}</span>
            </div>
            <button onClick={()=>remind(p)} disabled={!p.student_email||sent[p.id]==='sending'}
              style={{ fontSize:'11px', fontWeight:700, padding:'7px 12px', borderRadius:'9px', border:'none', cursor:p.student_email?'pointer':'not-allowed', fontFamily:'inherit', flexShrink:0, transition:'all 0.2s', background:sent[p.id]==='sent'?'rgba(16,185,129,0.15)':!p.student_email?'rgba(var(--fg-rgb),0.04)':'rgba(244,163,53,0.15)', color:sent[p.id]==='sent'?'#34d399':!p.student_email?'rgba(var(--fg-rgb),var(--a20))':'#f4a335' }}>
              {sent[p.id]==='sending'?'⏳':sent[p.id]==='sent'?'✓ Sent':!p.student_email?'No email':'🔔 Remind'}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── USERS SECTION WITH DELETE ─────────────────────────────────────────────────
function UsersSection({ profile, users, loading, isMobile, onRoleChange, onReload }) {
  const [search,       setSearch]       = React.useState('')
  const [filterRole,   setFilterRole]   = React.useState('all')
  const [filterStatus, setFilterStatus] = React.useState('active')
  const [deleteModal,  setDeleteModal]  = React.useState(null)
  const [restoreId,    setRestoreId]    = React.useState(null)
  const [busy,         setBusy]         = React.useState(false)
  const [ok,           setOk]           = React.useState('')
  const [err,          setErr]          = React.useState('')
  const [allUsers,     setAllUsers]     = React.useState([])
  const [loadingAll,   setLoadingAll]   = React.useState(true)
  const [otpPending,   setOtpPending]   = React.useState(null) // delete type ('temporary'|'permanent') awaiting OTP
  const [otpCode,      setOtpCode]      = React.useState('')
  const [otpBusy,      setOtpBusy]      = React.useState(false)
  const [otpErr,       setOtpErr]       = React.useState('')
  const [otpSent,      setOtpSent]      = React.useState(false)
  const [deleteImpact, setDeleteImpact] = React.useState(null) // { soleClassIds, totalClasses } for the student being deleted
  const [impactLoading,setImpactLoading]= React.useState(false)
  const [cleanupClasses, setCleanupClasses] = React.useState(true)

  React.useEffect(() => { loadAllUsers() }, [])

  // A student's classes get orphaned (left on the teacher's calendar with
  // nobody enrolled) if we don't also clean them up here — deleting a
  // profile never touches the classes/enrollments tables on its own.
  async function openDeleteModal(u) {
    setDeleteModal(u); setOtpPending(null); setOtpCode(''); setOtpErr(''); setOtpSent(false)
    setDeleteImpact(null); setCleanupClasses(true)
    if (u.role !== 'student') return
    setImpactLoading(true)
    try {
      const { data: enr } = await supabase.from('enrollments').select('class_id').eq('student_id', u.id)
      const classIds = [...new Set((enr||[]).map(e => e.class_id).filter(Boolean))]
      if (!classIds.length) { setDeleteImpact({ totalClasses: 0, soleClassIds: [] }); setImpactLoading(false); return }

      const { data: allEnr } = await supabase.from('enrollments').select('class_id,student_id').in('class_id', classIds)
      const countByClass = {}
      ;(allEnr||[]).forEach(e => { countByClass[e.class_id] = (countByClass[e.class_id]||0) + 1 })
      const soleClassIds = classIds.filter(id => (countByClass[id]||0) <= 1)

      setDeleteImpact({ totalClasses: classIds.length, soleClassIds })
    } catch(e) {
      setDeleteImpact({ totalClasses: 0, soleClassIds: [] })
    }
    setImpactLoading(false)
  }

  async function requestDeleteOtp(type) {
    setOtpErr(''); setOtpCode(''); setOtpSent(false); setOtpPending(type); setOtpBusy(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send', accessToken: session?.access_token }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Could not send code')
      setOtpSent(true)
    } catch (e) {
      setOtpErr(e.message)
    }
    setOtpBusy(false)
  }

  async function confirmDeleteOtp() {
    if (otpCode.length !== 6) return
    setOtpBusy(true); setOtpErr('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', accessToken: session?.access_token, code: otpCode }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Verification failed')
      const type = otpPending
      setOtpPending(null); setOtpCode(''); setOtpSent(false)
      await handleDelete(deleteModal, type)
    } catch (e) {
      setOtpErr(e.message)
    }
    setOtpBusy(false)
  }

  // Set once from the student's device at first login; fix it here when it's
  // wrong (e.g. the account was made in India). Clock Change relies on it.
  async function handleTimezone(user, timezone) {
    const { error } = await supabase.from('profiles').update({ timezone }).eq('id', user.id)
    if (error) setErr(error.message)
    else { setOk(`✓ ${user.full_name || user.email}: time zone updated.`); setTimeout(() => setOk(''), 4000); loadAllUsers() }
  }

  async function loadAllUsers() {
    setLoadingAll(true)
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false })
    setAllUsers(data || [])
    setLoadingAll(false)
  }

  const filtered = allUsers.filter(u => {
    const matchSearch = !search || u.full_name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
    const matchRole   = filterRole === 'all' || u.role === filterRole
    const matchStatus = filterStatus === 'active' ? !u.deleted_at : filterStatus === 'deleted' ? !!u.deleted_at : true
    return matchSearch && matchRole && matchStatus
  })

  const activeCount  = allUsers.filter(u => !u.deleted_at).length
  const deletedCount = allUsers.filter(u =>  u.deleted_at).length

  async function handleDelete(user, type) {
    setBusy(true); setErr(''); setOk('')
    try {
      const { error: delErr } = await supabase.from('profiles').update({
        deleted_at:  new Date().toISOString(),
        delete_type: type,
        deleted_by:  profile.id,
      }).eq('id', user.id)
      if (delErr) throw new Error(delErr.message)
      if (type === 'permanent') {
        try { await supabase.functions.invoke('delete-user', { body: { userId: user.id } }) } catch(_) {}
      }

      let cleanupMsg = ''
      const soleClassIds = deleteImpact?.soleClassIds || []
      if (cleanupClasses && soleClassIds.length) {
        const { data: soleClasses } = await supabase.from('classes').select('id,title').in('id', soleClassIds)
        await supabase.from('classes').delete().in('id', soleClassIds)
        for (const c of (soleClasses || [])) {
          const { error } = await supabase.from('events').delete().eq('class_id', c.id)
          if (error) await supabase.from('events').delete().eq('title', c.title).eq('event_type', 'class')
        }
        cleanupMsg = ` · ${soleClassIds.length} now-empty classes removed.`
      }

      setOk((type === 'permanent'
        ? `✓ ${user.full_name || user.email} permanently deleted.`
        : `✓ ${user.full_name || user.email} temporarily deleted. Data kept for 30 days.`) + cleanupMsg)
      setDeleteModal(null); setDeleteImpact(null)
      loadAllUsers(); onReload()
    } catch(e) { setErr(e.message) }
    setBusy(false)
    setTimeout(() => setOk(''), 5000)
  }

  async function handleRestore(user) {
    setRestoreId(user.id)
    await supabase.from('profiles').update({ deleted_at: null, delete_type: null, deleted_by: null }).eq('id', user.id)
    setOk(`✓ ${user.full_name || user.email} restored.`)
    setTimeout(() => setOk(''), 4000)
    setRestoreId(null)
    loadAllUsers(); onReload()
  }

  function daysLeft(deletedAt) {
    if (!deletedAt) return null
    return Math.max(0, 30 - Math.floor((Date.now() - new Date(deletedAt)) / 86400000))
  }

  const sel = { background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', color:'rgba(var(--fg-rgb),var(--a70))', borderRadius:'8px', padding:'6px 10px', fontSize:'12px', cursor:'pointer', outline:'none' }
  const inp = { background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'8px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit' }

  return (
    <>
      <PageHeader title="All Users" subtitle={`${activeCount} active · ${deletedCount} deleted`} />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:'10px', marginBottom:'16px' }}>
        {[
          { label:'Total',    value: allUsers.length,                                                      color:'var(--fg-solid)'     },
          { label:'Active',   value: activeCount,                                                           color:'#34d399'  },
          { label:'Students', value: allUsers.filter(u=>u.role==='student'&&!u.deleted_at).length,         color:'#5aabff'  },
          { label:'Teachers', value: allUsers.filter(u=>u.role==='teacher'&&!u.deleted_at).length,         color:'#a78bfa'  },
          { label:'Sales',    value: allUsers.filter(u=>u.role==='sales'&&!u.deleted_at).length,           color:'#f4a335'  },
          { label:'Deleted',  value: deletedCount, color: deletedCount > 0 ? '#f87171' : 'rgba(var(--fg-rgb),var(--a20))' },
        ].map(s => (
          <div key={s.label} style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'12px', padding:'12px', textAlign:'center' }}>
            <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.label}</div>
            <div style={{ fontSize:'22px', fontWeight:800, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {ok  && <div style={{ padding:'9px 14px', background:'rgba(16,185,129,0.1)',  border:'0.5px solid rgba(16,185,129,0.25)',  borderRadius:'9px', fontSize:'13px', color:'#34d399', marginBottom:'10px' }}>{ok}</div>}
      {err && <div style={{ padding:'9px 14px', background:'rgba(239,68,68,0.1)',   border:'0.5px solid rgba(239,68,68,0.25)',   borderRadius:'9px', fontSize:'13px', color:'#f87171', marginBottom:'10px' }}>{err}</div>}

      <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center' }}>
        <input style={{ ...inp, flex:1, minWidth:'180px' }} placeholder="🔍 Search name or email..." value={search} onChange={e=>setSearch(e.target.value)} />
        <select value={filterRole} onChange={e=>setFilterRole(e.target.value)} style={sel}>
          <option value="all">All Roles</option>
          <option value="student">Students</option>
          <option value="teacher">Teachers</option>
          <option value="sales">Sales</option>
        </select>
        <div style={{ display:'flex', gap:'4px' }}>
          {[['active','Active','#34d399'],['deleted','Deleted','#f87171'],['all','All','#5aabff']].map(([id,label,color]) => (
            <button key={id} onClick={()=>setFilterStatus(id)} style={{ fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'9px', border:'none', cursor:'pointer', fontFamily:'inherit', background: filterStatus===id?`${color}22`:'rgba(var(--fg-rgb),0.05)', color: filterStatus===id?color:'rgba(var(--fg-rgb),var(--a40))', borderBottom: filterStatus===id?`2px solid ${color}`:'2px solid transparent' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a25))', marginBottom:'8px' }}>{filtered.length} user{filtered.length!==1?'s':''} shown</div>

      {loadingAll ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px', color:'rgba(var(--fg-rgb),var(--a20))' }}>No users found</div>
      ) : (
        <div style={{ display:'grid', gap:'6px' }}>
          {filtered.map(u => {
            const isDeleted   = !!u.deleted_at
            const isPermanent = u.delete_type === 'permanent'
            const days        = daysLeft(u.deleted_at)
            return (
              <div key={u.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'12px 14px', background: isDeleted?'rgba(239,68,68,0.05)':'rgba(var(--fg-rgb),0.03)', border:`0.5px solid ${isDeleted?'rgba(239,68,68,0.15)':'rgba(var(--fg-rgb),0.06)'}`, borderRadius:'12px', opacity: isDeleted?0.75:1, flexWrap: isMobile?'wrap':'nowrap' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'50%', flexShrink:0, background: isDeleted?'rgba(239,68,68,0.15)':'rgba(30,144,255,0.2)', color: isDeleted?'#f87171':'#5aabff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:800 }}>
                  {isDeleted ? '🗑' : (u.full_name||u.email||'?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'13px', color: isDeleted?'rgba(var(--fg-rgb),var(--a40))':'rgba(var(--fg-rgb),var(--a85))', fontWeight:600, textDecoration: isDeleted?'line-through':'none' }}>{u.full_name||'—'}</span>
                    {isDeleted && <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 6px', borderRadius:'4px', background: isPermanent?'rgba(239,68,68,0.2)':'rgba(244,163,53,0.2)', color: isPermanent?'#f87171':'#f4a335' }}>{isPermanent?'🗑 Permanent':`⏳ ${days}d left`}</span>}
                  </div>
                  {!isMobile && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'1px' }}>{u.email}</div>}
                </div>
                {!isDeleted && u.role === 'student' && (
                  <select value={u.timezone || ''} onChange={e=>handleTimezone(u, e.target.value)} title="Student's time zone" style={{ ...sel, maxWidth: isMobile?'130px':'190px' }}>
                    {!u.timezone && <option value="">🌍 Time zone not set</option>}
                    {u.timezone && !TIMEZONES.some(t => t.tz === u.timezone) && <option value={u.timezone}>🌍 {u.timezone}</option>}
                    {TIMEZONES.map(t => <option key={t.tz} value={t.tz}>🌍 {t.label}</option>)}
                  </select>
                )}
                {!isDeleted && u.role !== 'admin' && (
                  <select value={u.role} onChange={e=>onRoleChange(u.id, e.target.value)} style={{ ...sel, maxWidth: isMobile?'100px':'120px' }}>
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="sales">Sales</option>
                  </select>
                )}
                {!isDeleted ? (
                  u.role === 'admin'
                    ? <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a20))', padding:'5px 10px', background:'rgba(var(--fg-rgb),0.04)', borderRadius:'8px', whiteSpace:'nowrap' }}>🔒 Admin</span>
                    : <button onClick={()=>openDeleteModal(u)} style={{ fontSize:'11px', fontWeight:700, padding:'6px 12px', borderRadius:'8px', border:'none', cursor:'pointer', background:'rgba(239,68,68,0.1)', color:'#f87171', fontFamily:'inherit', flexShrink:0, whiteSpace:'nowrap' }}>🗑 Delete</button>
                ) : (
                  !isPermanent && <button onClick={()=>handleRestore(u)} disabled={restoreId===u.id} style={{ fontSize:'11px', fontWeight:700, padding:'6px 12px', borderRadius:'8px', border:'none', cursor:'pointer', background:'rgba(16,185,129,0.12)', color:'#34d399', fontFamily:'inherit', flexShrink:0, opacity:restoreId===u.id?0.6:1 }}>{restoreId===u.id?'⏳':'↩ Restore'}</button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {deleteModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
          <div style={{ background:'var(--bg-page)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'16px', padding:'28px', width:'100%', maxWidth:'440px' }}>

            {otpPending ? (
              <>
                <div style={{ fontSize:'18px', fontWeight:800, color:'var(--fg-solid)', marginBottom:'6px' }}>🔐 Confirm with Email Code</div>
                <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a50))', marginBottom:'20px', lineHeight:1.6 }}>
                  For security, deleting <strong style={{ color:'var(--fg-solid)' }}>{deleteModal.full_name || deleteModal.email}</strong> ({otpPending}) needs a code sent to your admin email.
                </div>

                <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'6px' }}>Verification Code</label>
                <input
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  autoFocus
                  style={{ width:'100%', boxSizing:'border-box', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px', padding:'12px', fontSize:'22px', letterSpacing:'8px', textAlign:'center', fontWeight:700, color:'var(--fg-solid)', outline:'none', fontFamily:'inherit' }}
                />

                {otpErr && <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{otpErr}</div>}
                {otpSent && !otpErr && <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(16,185,129,0.1)', color:'#34d399', borderRadius:'8px', fontSize:'12px' }}>Code sent to {profile.email}</div>}

                <button onClick={confirmDeleteOtp} disabled={otpBusy || otpCode.length !== 6} style={{ width:'100%', marginTop:'14px', padding:'11px', background: otpCode.length===6 ? 'rgba(239,68,68,0.2)' : 'rgba(100,100,100,0.2)', color:'#f87171', border:'0.5px solid rgba(239,68,68,0.3)', borderRadius:'10px', fontSize:'13px', fontWeight:700, cursor: otpCode.length===6 ? 'pointer' : 'not-allowed', fontFamily:'inherit', opacity:otpBusy?0.6:1 }}>
                  {otpBusy ? '⏳ Verifying...' : `Verify & ${otpPending === 'permanent' ? 'Permanently' : 'Temporarily'} Delete`}
                </button>
                <button onClick={() => requestDeleteOtp(otpPending)} disabled={otpBusy} style={{ width:'100%', marginTop:'8px', padding:'9px', background:'transparent', border:'none', color:'rgba(var(--fg-rgb),var(--a35))', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' }}>
                  Didn't get it? Resend code
                </button>
                <button onClick={()=>{ setOtpPending(null); setOtpCode(''); setOtpErr(''); setOtpSent(false) }} style={{ width:'100%', marginTop:'6px', padding:'11px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Back</button>
              </>
            ) : (
              <>
                <div style={{ fontSize:'18px', fontWeight:800, color:'var(--fg-solid)', marginBottom:'6px' }}>🗑 Delete User</div>
                <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a50))', marginBottom:'20px' }}>
                  Choose how to delete <strong style={{ color:'var(--fg-solid)' }}>{deleteModal.full_name || deleteModal.email}</strong>
                </div>
                {err && <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px', marginBottom:'12px' }}>{err}</div>}

                {deleteModal.role === 'student' && (
                  impactLoading ? (
                    <div style={{ padding:'12px 14px', marginBottom:'12px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))' }}>Checking this student's classes...</div>
                  ) : deleteImpact?.soleClassIds?.length > 0 ? (
                    <div style={{ padding:'14px', marginBottom:'12px', background:'rgba(244,163,53,0.08)', border:'0.5px solid rgba(244,163,53,0.25)', borderRadius:'12px' }}>
                      <div style={{ fontSize:'12px', color:'#f4a335', fontWeight:700, marginBottom:'6px' }}>
                        ⚠ {deleteImpact.soleClassIds.length} of their {deleteImpact.totalClasses} classes will be left empty
                      </div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', lineHeight:1.5, marginBottom:'8px' }}>
                        This student is the only one enrolled in those classes. Unless removed, they'll stay on the teacher's schedule with nobody in them.
                      </div>
                      <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))', cursor:'pointer' }}>
                        <input type="checkbox" checked={cleanupClasses} onChange={e=>setCleanupClasses(e.target.checked)} />
                        Also remove those {deleteImpact.soleClassIds.length} now-empty classes
                      </label>
                    </div>
                  ) : deleteImpact?.totalClasses > 0 ? (
                    <div style={{ padding:'10px 14px', marginBottom:'12px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'10px' }}>
                      This student shares all {deleteImpact.totalClasses} of their classes with other students — none will be left empty.
                    </div>
                  ) : null
                )}

                <div style={{ padding:'16px', background:'rgba(244,163,53,0.06)', border:'0.5px solid rgba(244,163,53,0.2)', borderRadius:'12px', marginBottom:'10px' }}>
                  <div style={{ fontSize:'14px', fontWeight:700, color:'#f4a335', marginBottom:'4px' }}>⏳ Temporary Delete</div>
                  <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'12px', lineHeight:1.6 }}>User loses access immediately. All data kept for <strong style={{ color:'#f4a335' }}>30 days</strong> then auto-purged. Restorable within 30 days.</div>
                  <button onClick={()=>requestDeleteOtp('temporary')} disabled={busy} style={{ width:'100%', padding:'11px', background:'rgba(244,163,53,0.15)', color:'#f4a335', border:'0.5px solid rgba(244,163,53,0.3)', borderRadius:'10px', fontSize:'13px', fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity:busy?0.6:1 }}>
                    {busy?'⏳ Processing...':'⏳ Temporarily Delete (30 day recovery)'}
                  </button>
                </div>

                <div style={{ padding:'16px', background:'rgba(239,68,68,0.06)', border:'0.5px solid rgba(239,68,68,0.2)', borderRadius:'12px', marginBottom:'16px' }}>
                  <div style={{ fontSize:'14px', fontWeight:700, color:'#f87171', marginBottom:'4px' }}>⛔ Permanent Delete</div>
                  <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'12px', lineHeight:1.6 }}>User and ALL their data is <strong style={{ color:'#f87171' }}>permanently deleted immediately</strong>. Cannot be undone.</div>
                  <button onClick={()=>{ if(window.confirm(`PERMANENT DELETE: Are you absolutely sure you want to permanently delete "${deleteModal.full_name||deleteModal.email}"? This CANNOT be undone.`)) requestDeleteOtp('permanent') }} disabled={busy} style={{ width:'100%', padding:'11px', background:'rgba(239,68,68,0.15)', color:'#f87171', border:'0.5px solid rgba(239,68,68,0.3)', borderRadius:'10px', fontSize:'13px', fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity:busy?0.6:1 }}>
                    {busy?'⏳ Processing...':'⛔ Permanently Delete (cannot undo)'}
                  </button>
                </div>

                <button onClick={()=>{setDeleteModal(null);setErr('')}} style={{ width:'100%', padding:'11px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                <div style={{ marginTop:'12px', padding:'10px 12px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'8px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', lineHeight:1.6 }}>
                  🔒 Admin accounts cannot be deleted here. Use Supabase Dashboard to manage admins.
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
